import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { type ImageCardTagInput, buildImageCardTags, resolveImageFileSize } from '@/components-biz/ImageSelect/utils/imageCardDisplay';

import type { ResourceSummaryItem } from '../resourceConfig/types';

/** 镜像卡片摘要数据源（与 ImageCard Tag 展示规则对齐） */
export interface ImageSummarySource {
  displayName?: string;
  version?: string;
  /** 兼容旧字段：等价 vendor */
  os?: string;
  vendor?: string;
  /** 兼容旧字段：等价 cpuArchitectures */
  architecture?: string;
  cpuArchitectures?: string | string[];
  artifactType?: string;
  packageFormat?: string;
  dockerSourceSize?: string;
  httpSourceSize?: string;
  fileSize?: string;
  systemDiskSize?: string | number;
  kernelVersion?: string;
  bootloader?: string;
}

function normalizeSummaryTagInput(source: ImageSummarySource): ImageCardTagInput {
  return {
    artifactType: source.artifactType,
    packageFormat: source.packageFormat,
    vendor: source.vendor ?? source.os,
    cpuArchitectures: source.cpuArchitectures ?? source.architecture,
    dockerSourceSize: source.dockerSourceSize,
    httpSourceSize: source.httpSourceSize,
    kernelVersion: source.kernelVersion,
    systemDiskSize: source.systemDiskSize,
    bootloader: source.bootloader,
  };
}

function resolveSummaryDisplayName(source: ImageSummarySource): string {
  const name = String(source.displayName ?? '').trim();
  const version = String(source.version ?? '').trim();
  if (name && version) return `${name}: ${version}`;
  return name || version || '-';
}

/** 镜像卡片展示名：优先名称字段，无名称再回退地址 / key */
export function resolveImageCardDisplayName(
  formValues: Record<string, unknown>,
  options: {
    imageKey?: string;
    imageUrl?: string;
    imagePrefix?: string;
  },
): string {
  const imageKey = String(options.imageKey ?? '').trim();
  const imageUrl = String(options.imageUrl ?? '').trim();
  const prefix = options.imagePrefix?.replace(/\.$/, '');

  const nameCandidates = [
    prefix ? get(formValues, `${prefix}.imageName`) : undefined,
    prefix ? get(formValues, `${prefix}.name`) : undefined,
    get(formValues, 'imageNickname'),
  ];

  for (const candidate of nameCandidates) {
    const trimmed = String(candidate ?? '').trim();
    if (trimmed) return trimmed;
  }

  return imageUrl || imageKey;
}

/** 构造 ResourceConfigCard 所需的镜像摘要行 */
export function buildImageResourceSummaryItems(source: ImageSummarySource): ResourceSummaryItem[] {
  const items: ResourceSummaryItem[] = [{ label: '', value: resolveSummaryDisplayName(source) }];

  const tagInput = normalizeSummaryTagInput(source);
  if (source.fileSize && !resolveImageFileSize(tagInput)) {
    tagInput.dockerSourceSize = source.fileSize;
  }

  buildImageCardTags(tagInput).forEach((tag) => {
    items.push({ label: tag.label, value: tag.value });
  });

  return items;
}

/** 从镜像列表项解析摘要（选中后 / preset 回显） */
export function resolveImageSummaryFromItem(item: CommonImageItemType): ImageSummarySource {
  return {
    displayName: item.name,
    version: item.version,
    vendor: item.vendor,
    cpuArchitectures: item.cpuArchitectures,
    artifactType: item.artifactType,
    packageFormat: item.packageFormat,
    dockerSourceSize: item.dockerSourceSize,
    httpSourceSize: item.httpSourceSize,
    systemDiskSize: get(item, 'options.systemImage.systemVolumeSize'),
    kernelVersion: item.kernelVersion,
    bootloader: get(item, 'options.systemImage.bootloader'),
  };
}

/** 从容器镜像表单字段解析摘要 */
export function resolveImageSummaryFromContainerForm(
  formValues: Record<string, unknown>,
  paths: { imageKey: string; imageUrl: string; imagePrefix: string },
  architectureOverride?: string,
): ImageSummarySource | null {
  const imageKey = String(get(formValues, paths.imageKey) ?? '').trim();
  const imageUrl = String(get(formValues, paths.imageUrl) ?? '').trim();
  if (!imageKey && !imageUrl) return null;

  const prefix = paths.imagePrefix;
  const displayName = resolveImageCardDisplayName(formValues, {
    imageKey,
    imageUrl,
    imagePrefix: prefix,
  });

  return {
    displayName,
    version: String(get(formValues, `${prefix}.version`) ?? '').trim() || undefined,
    vendor: String(get(formValues, `${prefix}.os`) ?? get(formValues, `${prefix}.vendor`) ?? '').trim() || undefined,
    cpuArchitectures: String(architectureOverride ?? get(formValues, `${prefix}.architecture`) ?? '').trim() || undefined,
    artifactType: String(get(formValues, `${prefix}.artifactType`) ?? '').trim() || undefined,
    packageFormat: String(get(formValues, `${prefix}.packageFormat`) ?? '').trim() || undefined,
    dockerSourceSize: String(get(formValues, `${prefix}.dockerSourceSize`) ?? '').trim() || undefined,
    httpSourceSize: String(get(formValues, `${prefix}.httpSourceSize`) ?? '').trim() || undefined,
    systemDiskSize: get(formValues, `${prefix}.systemDiskSize`) as string | number | undefined,
    kernelVersion: String(get(formValues, `${prefix}.kernelVersion`) ?? '').trim() || undefined,
    bootloader: String(get(formValues, `${prefix}.bootloader`) ?? '').trim() || undefined,
  };
}
